import { useRouter } from '@tanstack/react-router';
import { useLayoutEffect } from 'react';

const STORAGE_KEY = 'main-scroll-positions';

/**
 * Custom scroll restoration for the <main data-scroll-restoration-id="main-scroll-container">
 * element.
 *
 * TanStack Router's built-in <ScrollRestoration /> saves element-scroll positions
 * under the internal `state.key`. When `replace: true` is used (as in AppBackService's
 * executeAppBack), TanStack generates a new key for the replaced entry, so the saved
 * scroll position is lost on subsequent back-navigation.
 *
 * This hook provides a belt-and-suspenders mechanism keyed by the URL href (which
 * stays stable across replace:true navigation):
 *  - Saves scrollTop on every scroll event (throttled) under the current href.
 *  - Restores scrollTop in useLayoutEffect (synchronous, before paint) on mount.
 *  - Also saves on beforeNavigate to capture the position just before leaving.
 *
 * Works alongside TanStack's <ScrollRestoration /> — the built-in handles forward
 * navigation, this hook handles the replace:true back-navigation case.
 */
export function useMainScrollRestoration(): void {
  const router = useRouter();

  // Restore on mount (catches history.back() / forward() / replace:true back)
  useLayoutEffect(() => {
    const container = document.querySelector<HTMLElement>(
      '[data-scroll-restoration-id="main-scroll-container"]',
    );
    if (!container) return;

    const saved = loadScrollPosition(router.state.location.href);
    if (saved != null) {
      container.scrollTop = saved;
    }
  }, [router.state.location.href]);

  // Save on scroll (throttled)
  useLayoutEffect(() => {
    const container = document.querySelector<HTMLElement>(
      '[data-scroll-restoration-id="main-scroll-container"]',
    );
    if (!container) return;

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          saveScrollPosition(router.state.location.href, container.scrollTop);
          ticking = false;
        });
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [router.state.location.href]);

  // Save on navigation (belt-and-suspenders — captures position just before leave)
  useLayoutEffect(() => {
    const unsub = router.subscribe('onBeforeNavigate', () => {
      const container = document.querySelector<HTMLElement>(
        '[data-scroll-restoration-id="main-scroll-container"]',
      );
      if (!container) return;
      saveScrollPosition(router.state.location.href, container.scrollTop);
    });
    return unsub;
  }, [router]);
}

function loadScrollPosition(key: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, number>;
    return map[key] ?? null;
  } catch {
    return null;
  }
}

function saveScrollPosition(key: string, scrollTop: number): void {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    map[key] = scrollTop;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Silently ignore (e.g. private browsing)
  }
}
